import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';
import logoMark from '../../shared/assets/logos/BlackLogoPNG_TransparentBackground.png';
import NetworkCanvas from '../../shared/components/NetworkCanvas';

const Login = () => {
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Auth0 login error:', err);
      const desc = err.description;
      const msg = (typeof desc === 'string' ? desc : null) || err.message || 'Login failed.';
      if (msg.includes('Wrong email or password') || msg.includes('invalid_grant') || err.code === 'access_denied') {
        setError('Invalid email or password.');
      } else {
        setError(msg);
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex relative overflow-hidden">
      {/* Mobile-only background logo mark */}
      <div
        className={`absolute right-[-15%] top-1/2 -translate-y-1/2 lg:hidden transition-all duration-[1500ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          loaded ? 'opacity-[0.025] scale-100' : 'opacity-0 scale-[0.95]'
        }`}
        style={{ transitionDelay: '300ms' }}
      >
        <img
          src={logoMark}
          alt=""
          className="w-[20rem] sm:w-[28rem] h-auto select-none pointer-events-none"
          draggable="false"
        />
      </div>

      {/* Left — form side */}
      <div className="w-full lg:w-5/12 min-h-screen flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 relative z-10">
        {/* Logo */}
        <div
          className={`mb-16 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <Link to="/">
            <img
              src={logo}
              alt="Arbcor"
              className="h-6 w-auto select-none pointer-events-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
          </Link>
        </div>

        {/* Eyebrow */}
        <div
          className={`flex items-center gap-4 mb-6 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
          style={{ transitionDelay: '400ms' }}
        >
          <div className="h-px w-12 bg-zinc-900" />
          <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Welcome Back</span>
        </div>

        {/* Headline */}
        <h1
          className={`text-[clamp(2rem,4vw,3rem)] font-light text-zinc-900 leading-[1.1] mb-2 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          Sign in to
        </h1>
        <p
          className={`text-[clamp(2rem,4vw,3rem)] font-extralight text-zinc-300 leading-[1.1] mb-10 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          your account.
        </p>

        {/* Divider */}
        <div
          className="h-px bg-zinc-200 mb-10 transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ width: loaded ? '80px' : '0px', transitionDelay: '800ms' }}
        />

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className={`space-y-4 max-w-sm transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '900ms' }}
        >
          <div>
            <label className="block text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="you@company.com"
              autoComplete="email"
              disabled={submitting}
              className={`w-full px-4 py-3 border text-sm text-zinc-900 placeholder-zinc-300 outline-none transition-colors duration-300 disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
              }`}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">
                Password
              </label>
              <button
                type="button"
                className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors duration-300"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={submitting}
                className={`w-full px-4 py-3 pr-10 border text-sm text-zinc-900 placeholder-zinc-300 outline-none transition-colors duration-300 disabled:opacity-50 ${
                  error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <p className="text-red-400 text-xs mt-1">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-2 px-4 py-3 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Cross-page link */}
        <div
          className={`mt-8 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '1100ms' }}
        >
          <p className="text-zinc-400 text-sm">
            Don't have an account?{' '}
            <Link to="/signup" className="text-zinc-900 hover:text-zinc-600 transition-colors duration-300">
              Sign up
            </Link>
          </p>
        </div>

        {/* Back to home */}
        <div
          className={`mt-12 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '1200ms' }}
        >
          <Link
            to="/"
            className="group inline-flex items-center gap-3 text-sm tracking-wider text-zinc-400 uppercase hover:text-zinc-600 transition-colors duration-300"
          >
            <span className="inline-block w-8 h-px bg-zinc-300 group-hover:w-12 transition-all duration-500" />
            Back to home
          </Link>
        </div>
      </div>

      {/* Right — network visualization */}
      <div className="hidden lg:flex w-7/12 min-h-screen bg-zinc-950 relative overflow-hidden items-center justify-center">
        <NetworkCanvas />
      </div>
    </div>
  );
};

export default Login;
